import { NavLink } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarCheck,
  CreditCard,
  FileText,
  Inbox,
  Repeat,
  ScrollText,
  Settings,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { hu } from '@/lib/i18n/hu'

interface Item {
  to: string
  icon: React.ComponentType<{ className?: string }>
  label: string
}

const ITEMS: Item[] = [
  { to: '/invoices', icon: FileText, label: hu.nav.invoices },
  { to: '/banking', icon: CreditCard, label: hu.nav.banking },
  { to: '/recurring', icon: Repeat, label: 'Visszatérők' },
  { to: '/reimbursements', icon: Wallet, label: 'Elszámolások' },
  { to: '/project-pnl', icon: TrendingUp, label: 'Projekt P&L' },
  { to: '/discrepancies', icon: AlertTriangle, label: hu.nav.discrepancies },
  { to: '/monthly-close', icon: CalendarCheck, label: hu.nav.monthlyClose },
  { to: '/inbox', icon: Inbox, label: hu.nav.inbox },
  { to: '/settings', icon: Settings, label: hu.nav.settings },
]

export function Sidebar() {
  return (
    <aside
      className={cn(
        'hidden md:flex md:w-56 lg:w-60 flex-col min-h-screen sticky top-0 z-20',
        'border-r border-slate-200/80 dark:border-slate-800/60',
        // Subtle gradient — kicsit "felemelt" érzet a fő canvas-tól
        'bg-gradient-to-b from-white to-slate-50/80 dark:from-slate-950/80 dark:to-slate-950',
      )}
    >
      {/* Brand block */}
      <div className="flex h-14 items-center gap-2.5 px-4 border-b border-slate-200/60 dark:border-slate-800/60">
        <img
          src="/adaptivo-mark.svg"
          alt="Adaptivo"
          width={36}
          height={36}
          className="h-9 w-9 shrink-0 drop-shadow-[0_0_10px_rgba(34,211,238,0.35)]"
        />
        <div className="leading-tight">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 tracking-tightish">
            Adaptivo
          </div>
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            Számla modul
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2.5 py-4" aria-label="Fő navigáció">
        <ul className="space-y-1">
          {ITEMS.map(({ to, icon: Icon, label }) => (
            <li key={to}>
              <NavItem to={to} Icon={Icon} label={label} />
            </li>
          ))}
        </ul>

        <div className="mt-6 mb-2 px-2.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 dark:text-slate-500">
          Egyéb
        </div>
        <ul className="space-y-1">
          <li>
            <NavItem to="/settings/audit" Icon={ScrollText} label={hu.nav.auditLog} />
          </li>
        </ul>
      </nav>

    </aside>
  )
}

function NavItem({
  to,
  Icon,
  label,
  onClick,
}: {
  to: string
  Icon: React.ComponentType<{ className?: string }>
  label: string
  onClick?: () => void
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium',
          'transition-all duration-150 ease-out-soft',
          isActive
            ? 'text-brand-700 dark:text-brand-200 bg-brand-50/80 dark:bg-brand-950/40'
            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100/60 dark:hover:bg-slate-800/40',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={cn(
              'absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-all duration-150',
              isActive ? 'bg-gradient-to-b from-brand-500 to-accent-500 opacity-100' : 'opacity-0',
            )}
          />
          <Icon
            className={cn(
              'h-4 w-4 shrink-0 transition-colors',
              isActive
                ? 'text-brand-600 dark:text-brand-300'
                : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300',
            )}
          />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  )
}

/**
 * Mobile drawer-style sidebar — kis képernyőn TopNav hamburger gombja nyitja.
 * A backdrop kattintásra zár, ESC-re is.
 */
export function MobileSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-fade-in-up"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          'absolute left-0 top-0 h-full w-64 z-10',
          'bg-white dark:bg-slate-950 border-r border-slate-200/80 dark:border-slate-800/60 shadow-lift',
          'flex flex-col animate-fade-in-up',
        )}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="flex h-14 items-center gap-2.5 px-4 border-b border-slate-200/60 dark:border-slate-800/60">
          <img
            src="/adaptivo-mark.svg"
            alt="Adaptivo"
            width={36}
            height={36}
            className="h-9 w-9 shrink-0 drop-shadow-[0_0_10px_rgba(34,211,238,0.35)]"
          />
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 tracking-tightish">
              Adaptivo
            </div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              Számla modul
            </div>
          </div>
        </div>
        <nav className="flex-1 px-2.5 py-4" aria-label="Mobil navigáció">
          <ul className="space-y-1">
            {ITEMS.map(({ to, icon: Icon, label }) => (
              <li key={to}>
                <NavItem to={to} Icon={Icon} label={label} onClick={onClose} />
              </li>
            ))}
            <li>
              <NavItem to="/settings/audit" Icon={ScrollText} label={hu.nav.auditLog} onClick={onClose} />
            </li>
          </ul>
        </nav>
      </aside>
    </div>
  )
}
